import { useController, type Control, type FieldValues, type Path } from 'react-hook-form';
import { Stack, SimpleGrid, TextInput, PasswordInput, Select, Text, Loader } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { CPUSER_CONSTRAINTS } from '@/lib/constants/account';
import { useUploadProfiles } from '../../hooks/useUploadProfiles';
import { useAccountPermissions } from '../../useAccountPermissions';

interface TeamMemberFormFieldsProps<T extends FieldValues> {
  control: Control<T>;
  isCreate: boolean;
  usernameStatus?: 'checking' | 'available' | 'taken' | null;
}

/**
 * Form fields for team member create/edit.
 * Generic over the form values type to support both create and edit schemas.
 */
export function TeamMemberFormFields<T extends FieldValues>({
  control,
  isCreate,
  usernameStatus,
}: TeamMemberFormFieldsProps<T>) {
  const { uploadProfiles } = useUploadProfiles();
  const { canViewUploadProfiles } = useAccountPermissions();

  const profileOptions = uploadProfiles.map(p => ({
    value: String(p.ingestcfgId),
    label: p.label,
  }));

  return (
    <Stack gap="md">
      <Text fw={600} size="lg">Account Details</Text>

      {isCreate && (
        <>
          <ControlledTextInput
            control={control}
            name={'username' as Path<T>}
            label="Username"
            maxLength={CPUSER_CONSTRAINTS.USERNAME_MAX}
            required
            description={
              usernameStatus === 'checking' ? 'Checking availability...' :
              usernameStatus === 'available' ? 'Username is available' :
              usernameStatus === 'taken' ? 'Username is already taken' : undefined
            }
            rightSection={
              usernameStatus === 'checking' ? <Loader size={14} /> :
              usernameStatus === 'available' ? <IconCheck size={16} color="var(--mantine-color-green-6)" /> :
              usernameStatus === 'taken' ? <IconX size={16} color="var(--mantine-color-red-6)" /> : undefined
            }
            forceLowercase
          />
          <SimpleGrid cols={2}>
            <ControlledPasswordInput
              control={control}
              name={'password' as Path<T>}
              label="Password"
              maxLength={CPUSER_CONSTRAINTS.PASSWORD_MAX}
              required
            />
            <ControlledPasswordInput
              control={control}
              name={'confirmPassword' as Path<T>}
              label="Confirm Password"
              maxLength={CPUSER_CONSTRAINTS.PASSWORD_MAX}
              required
            />
          </SimpleGrid>
        </>
      )}

      <SimpleGrid cols={2}>
        <ControlledTextInput
          control={control}
          name={'firstName' as Path<T>}
          label="First Name"
          maxLength={CPUSER_CONSTRAINTS.FIRST_NAME_MAX}
          required
        />
        <ControlledTextInput
          control={control}
          name={'lastName' as Path<T>}
          label="Last Name"
          maxLength={CPUSER_CONSTRAINTS.LAST_NAME_MAX}
          required
        />
      </SimpleGrid>
      <SimpleGrid cols={2}>
        <ControlledTextInput
          control={control}
          name={'email' as Path<T>}
          label="Email"
          maxLength={CPUSER_CONSTRAINTS.EMAIL_MAX}
          required
          forceLowercase
        />
        <ControlledTextInput
          control={control}
          name={'phone' as Path<T>}
          label="Phone"
          maxLength={CPUSER_CONSTRAINTS.PHONE_MAX}
        />
      </SimpleGrid>

      {canViewUploadProfiles && (
        <SimpleGrid cols={2}>
          <ControlledSelect
            control={control}
            name={'ingestcfgId' as Path<T>}
            label="Upload Profile"
            description={profileOptions.length > 0 ? undefined : 'No upload profiles configured'}
            placeholder="Any (default)"
            data={profileOptions}
            clearable
          />
        </SimpleGrid>
      )}
    </Stack>
  );
}

function ControlledTextInput<T extends FieldValues>({
  control,
  name,
  label,
  maxLength,
  required,
  description,
  rightSection,
  forceLowercase,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  maxLength?: number;
  required?: boolean;
  description?: string;
  rightSection?: React.ReactNode;
  forceLowercase?: boolean;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <TextInput
      label={label}
      value={field.value ?? ''}
      onChange={(e) => {
        const val = forceLowercase ? e.currentTarget.value.toLowerCase() : e.currentTarget.value;
        field.onChange(val);
      }}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      maxLength={maxLength}
      withAsterisk={required}
      description={description}
      rightSection={rightSection}
    />
  );
}

function ControlledPasswordInput<T extends FieldValues>({
  control,
  name,
  label,
  maxLength,
  required,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  maxLength?: number;
  required?: boolean;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <PasswordInput
      label={label}
      value={field.value ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      maxLength={maxLength}
      withAsterisk={required}
    />
  );
}

function ControlledSelect<T extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder,
  data,
  clearable,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  description?: string;
  placeholder?: string;
  data: { value: string; label: string }[];
  clearable?: boolean;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <Select
      label={label}
      description={description}
      placeholder={placeholder}
      data={data}
      value={field.value != null ? String(field.value) : null}
      onChange={(val) => field.onChange(val ? Number(val) : null)}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      clearable={clearable}
      searchable
    />
  );
}
